//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { Fragment, useState, useCallback, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { useHistory, useLocation } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { PATH_DASHBOARD } from '../system/constants';
import { AppContext } from '../common/contexts';
import { LayoutContext } from '../common/contexts';
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { setClassNameInDefns } from '../common/definitionHelper';
import { DASHBOARD_MY_SETTINGS } from '../common/constants';
import { BASE_URL } from '../common/constants';
import { handleCloseSnackbar } from '../common/snackbarHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
// import { activeEmpMemberAfterSetUp } from '../common/services/authServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdSnackbar from '../common/elemental/AfitdSnackbar';
import TopNav from '../topNav/TopNav';
import EditProfileDialog, { VARIANT_MEMBER_SETUP } from '../empDashboardMySettings/subComponents/EditProfileDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import AccountSetupLeftNavigation from './subComponents/AccountSetupLeftNavigation';
import AfitdLink from '../common/elemental/AfitdLink';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _pick from 'lodash/pick';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    dashboardContainer: { overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset' },
    dashboardHeader: { maxHeight: '81px' },
    dashboardContent: { flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingRight: '64px', [theme.breakpoints.down('md')]: { paddingRight: '0px', justifyContent: 'center' } },
    dashboardRight: { height: '100%', [theme.breakpoints.down('lg')]: { height: '735px' }, [theme.breakpoints.down('md')]: { height: '100%' } },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        appContext,
        // props,
        classes,
        setupModalOpen,
        // handleSavedSetUpInfoModal,
        handleCloseSetUpInfoModal,
        snackbarMessage,
        setSnackbarMessage,
        snackbarErrorMessage,
        setSnackbarErrorMessage,
    } = params;
    // const {} = props;

    const { userId, currentUser, currentEmployerIdx } = appContext;
    const userEmployerData = _pick(_get(currentUser, `employers[${currentEmployerIdx}]`), [
        'profilePicUrl',
        'firstName',
        'middleName',
        'lastName',
        'country',
        'state',
        'city',
        'cityLocation',
    ]);

    let defns = {
        dashboardContainer: { container: true, direction: 'column' },
        dashboardHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        dashboardContent: { item: true, container: true },
        dashboardLeft: { item: true, lg: 3, md: 0, sm: 0, xs: 0 },
        dashboardRight: { item: true, lg: 9, md: 10, sm: 11, xs: 11 },

        setUpInfoModal: {
            openEditModal: setupModalOpen,
            variant: VARIANT_MEMBER_SETUP,
            onClose: handleCloseSetUpInfoModal,
            onCancel: handleCloseSetUpInfoModal,
            // onDidSave: handleSavedSetUpInfoModal,
            memberUserId: userId,
            ...userEmployerData,
            // onCancel,
            // onClose,
            // onProfilePicDelete,
            // onProfilePicSave,
        },

        snackbar: {
            message: { successMessage: snackbarMessage, errorMessage: snackbarErrorMessage },
            onClose: handleCloseSnackbar.bind(null, setSnackbarMessage, setSnackbarErrorMessage),
        },
        leftNavigation: {},
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const MemberSetupLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { userId } = appContext;

    const layoutContext = useContext(LayoutContext);

    const classes = useStyles();
    const defaultPage = DASHBOARD_MY_SETTINGS;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [dashboard, setDashboard] = useState(DASHBOARD_MY_SETTINGS);
    const [dashboard, setDashboard] = useState(defaultPage);
    const [snackbarMessage, setSnackbarMessage] = useState();
    const [snackbarErrorMessage, setSnackbarErrorMessage] = useState();
    const [setupModalOpen, setSetupModalOpen] = useState(true);

    const location = useLocation();

    //---------------------------------------------------------------------
    // TEMP TEMP TEMP TEMP TEMP TEMP
    //---------------------------------------------------------------------
    //---------------------------------------------------------------------
    //---------------------------------------------------------------------
    //---------------------------------------------------------------------
    const history = useHistory();
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const setCurrentDashboard = useCallback(
        newPageId => {
            // setDashboard(newDashboard);
            history.push(`${BASE_URL}${PATH_DASHBOARD}/${newPageId}`);
        },
        [history],
    );

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    // const handleSavedSetUpInfoModal = async () => {
    //     await activeEmpMemberAfterSetUp({ userId, employerId });
    // };

    const handleCloseSetUpInfoModal = () => {
        setSetupModalOpen(false);
    };

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // useEffect(() => {

    // },[])

    useEffect(() => {
        const { state } = location;
        if (state?.state?.currentDashboard) {
            return setCurrentDashboard(state.state.currentDashboard);
        }
    }, [location, setCurrentDashboard]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({
        appContext,
        props,
        classes,
        setupModalOpen,
        handleCloseSetUpInfoModal,
        // handleSavedSetUpInfoModal,
        snackbarMessage,
        setSnackbarMessage,
        snackbarErrorMessage,
        setSnackbarErrorMessage,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider
            value={{
                ...layoutContext,
                userId,
                dashboard,
                tempSetDashboard: setDashboard,
                setCurrentDashboard,
                setSnackbarMessage,
                setSnackbarErrorMessage,
            }}
        >
            <Fragment>
                <Grid {...defns.dashboardContainer}>
                    <Grid {...defns.dashboardHeader}>
                        <TopNav />
                    </Grid>
                    <Grid {...defns.dashboardContent}>
                        <Grid {...defns.dashboardLeft}>
                            <AccountSetupLeftNavigation {...defns.leftNavigation} />
                        </Grid>
                        <Grid {...defns.dashboardRight}>
                            <AfitdLink
                                onClick={() => {
                                    setSetupModalOpen(true);
                                }}
                            >
                                Open Setup Modal
                            </AfitdLink>
                        </Grid>
                    </Grid>
                </Grid>
                <AfitdSnackbar {...defns.snackbar} />
                {setupModalOpen && <EditProfileDialog {...defns.setUpInfoModal} />}
            </Fragment>
        </LayoutContext.Provider>
    );
};

export default MemberSetupLayout;
